import HelpIcon from '@mui/icons-material/Help'
import { Divider, Grid2, Slide, Typography } from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import Currency from 'components/Currency'
import { dashboardPath, tradePath } from 'components/Routes'
import CustomTooltip from 'components/Tooltip/Tooltip'
import TradingPaused from 'components/TradingPaused/TradingPaused'
import DefaultLayout from 'components/layout/DefaultLayout'
import { REVIEW_ORDER_EXPIRED, TRADING_IS_PAUSED } from 'constants/index'
import { ReviewOrder } from 'api/trading'
import { useMutateCreateMarketTransaction } from 'hooks/useTrading'
import useProducts from 'hooks/useProducts'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formattedPrice } from 'utils/util'
import { formatWeightAsGrams } from 'utils/formatWeightAsGrams'
import useStyles from './styles'

export interface Props {
  reviewOrderData?: ReviewOrder
  reviewOrderId: string
  reviewOrderLoading?: boolean
}

export const FEE_PLAN_TYPES: { [key: string]: string } = {
  INSURANCE: 'Standard Insurance Fee',
  TRANSACTION: 'Standard Transaction Fee'
}

export default function ReviewSale({
  reviewOrderData,
  reviewOrderId,
  reviewOrderLoading
}: Props): JSX.Element {
  const { classes } = useStyles()
  const { setToast, refreshCurrentUser } = useAuth()
  const navigate = useNavigate()

  const [tradingPaused, setTradingPaused] = useState<boolean>(false)

  const fees = reviewOrderData?.fees
  const totalPrice = reviewOrderData?.totalPrice
  const totalFees = reviewOrderData?.totalFees
  const quantityMg = reviewOrderData?.quantityMg
  const pricePerUnit = reviewOrderData?.pricePerUnit

  // totalPriceWithoutFees derived from totalPrice - totalFees
  const totalPriceWithoutFees =
    totalPrice != null && totalFees != null
      ? Number(totalPrice) - Number(totalFees)
      : undefined

  const { products } = useProducts()
  // The product being sold, as the order names it (GET /review_orders/{id}).
  // The first product in the catalog was only a guess: a silver sale was
  // labelled and sent back as gold, and while the catalog sorted by name the
  // first product was the painting. It stays the fallback for an order that
  // does not name its product.
  const product = reviewOrderData?.product ?? products[0]
  const productId = product?.id
  const unitType = product?.unitType ?? 'mg'
  const isMg = unitType === 'mg'
  const displayPricePerUnit = formattedPrice(
    isMg ? Number(pricePerUnit) / 1000 : Number(pricePerUnit)
  )

  const createTransactionMutation = useMutateCreateMarketTransaction()
  const createMarketTransactionLoading = createTransactionMutation.isLoading

  const handleConfirm = () => {
    if (productId) {
      createTransactionMutation.mutate(parseInt(reviewOrderId), {
        onSuccess: async () => {
          await refreshCurrentUser()
          setTradingPaused(false)
          setToast({
            open: true,
            message: `Good as gold! Market transaction created.`,
            type: 'success',
            duration: 3000
          })
          navigate(tradePath({ routeParams: { productId } }))
        },
        onError: (error: unknown) => {
          const err = error as { message?: string; errorStatus?: string }
          if (err?.errorStatus === TRADING_IS_PAUSED) {
            setTradingPaused(true)
          }
          setToast({
            open: true,
            message: err?.message ?? 'An error occurred',
            type: 'error',
            duration: 3000
          })
          if (err?.errorStatus === REVIEW_ORDER_EXPIRED) {
            navigate(dashboardPath())
          }
        }
      })
    }
  }

  const transactionFee = useMemo(() => {
    return fees?.find(fee => fee.label === FEE_PLAN_TYPES.TRANSACTION)?.amount
  }, [fees])

  const handleEditClick = () => {
    navigate(`/sell/${reviewOrderId}`)
  }

  const transactionInformation = [
    {
      label: `${product?.name} price per gram`,
      value: displayPricePerUnit,
      prefix: '$',
      unit: 'NZD'
    },
    {
      label: 'Milligrams to sell',
      value: formatWeightAsGrams(Number(quantityMg)),
      prefix: '',
      unit: ''
    },
    {
      label: 'Sale amount',
      value: formattedPrice(Number(totalPriceWithoutFees ?? 0)),
      prefix: '$',
      unit: 'NZD'
    }
  ]

  if (tradingPaused) {
    return <TradingPaused />
  }

  return (
    <DefaultLayout
      wrapperContent={{ mt: 0, mb: 2, px: 0 }}
      wrapperContainer={{ p: 0, position: 'relative' }}
      loading={reviewOrderLoading}
    >
      <Grid2>
        <Grid2 container mb={4}>
          <Slide direction='up' in={true} timeout={700}>
            <Grid2>
              <Typography component='h1' variant='h2' textAlign='center'>
                {formatWeightAsGrams(Number(quantityMg))} {product?.name}
              </Typography>
              <Typography component='h2' textAlign='center'>
                Double check your sale
              </Typography>
            </Grid2>
          </Slide>
        </Grid2>

        <Grid2 container spacing={2}>
          {transactionInformation.map(info => (
            <Grid2 container key={info.label}>
              <Grid2>
                <Typography className={classes.label}>{info.label}</Typography>
              </Grid2>
              <Grid2>
                <Typography className={classes.value}>
                  {info.prefix + info.value}
                  <Typography component='span'> {info.unit}</Typography>
                </Typography>
              </Grid2>
            </Grid2>
          ))}
        </Grid2>

        <Divider />

        <Grid2 container>
          <Grid2 container>
            <Grid2 gap={0.5}>
              <Typography className={classes.label}>Transaction fee</Typography>
              <CustomTooltip
                title='This is the cost to sell. It is 0.5% of the sale amount rounded to the nearest cent.'
                Icon={<HelpIcon color='primary' fontSize='small' />}
                iconSize='small'
              />
            </Grid2>
            <Grid2>
              <Typography className={classes.value}>
                {'$' + Number(transactionFee ?? 0).toFixed(2)}
                <Typography component='span'> NZD</Typography>
              </Typography>
            </Grid2>
          </Grid2>
        </Grid2>

        <Divider />

        <Grid2 container size={{ xs: 12 }}>
          <Grid2>
            <Typography component='p' variant='body1' fontWeight='700'>
              Total
            </Typography>
          </Grid2>
          <Grid2>
            <Typography component='p' variant='body1' fontWeight='700'>
              <Currency amount={Number(totalPrice ?? 0)} />
            </Typography>
          </Grid2>
        </Grid2>

        <Grid2 container mt={3} gap={1} className={classes.stickyFooter}>
          <Grid2 size={{ xs: 4 }}>
            <Button
              fullWidth
              onClick={handleEditClick}
              disabled={createMarketTransactionLoading}
              color='white'
            >
              edit
            </Button>
          </Grid2>
          <Grid2 flex={1}>
            <Button
              color='white'
              fullWidth
              type='submit'
              disabled={createMarketTransactionLoading}
              onClick={handleConfirm}
              loading={createMarketTransactionLoading}
            >
              Confirm Sell
            </Button>
          </Grid2>
        </Grid2>
      </Grid2>
    </DefaultLayout>
  )
}
